export type PlanIcon = 'droplet' | 'sparkle' | 'crown'
export type BenefitIcon =
  | 'piggy'
  | 'calendar'
  | 'unlock'
  | 'repeat'
  | 'zap'
  | 'message'
  | 'tag'
  | 'shield'

export interface MembershipPlan {
  id: string
  icon: PlanIcon
  name: string
  billing: string
  bestFor: string
  intro?: string
  features: string[]
  badge?: string
  featured: boolean
  cta: string
  optionLabel: string
}

const en = {
  kicker: 'Membership',
  title: 'Monthly Membership Plans',
  subtitle:
    'Save money. Keep your car looking pristine. Guaranteed twice-monthly washing included.',
  description:
    'One low monthly price, two professional washes every month — automatically scheduled and always reliable. Built for busy drivers who want a clean car without thinking about it.',
  perWash: '2 washes / month',
  plans: [
    {
      id: 'basic',
      icon: 'droplet',
      name: 'Basic Wash',
      billing: 'Billed monthly · cancel anytime',
      bestFor: 'Best for daily drivers on a budget',
      features: [
        'Exterior wash (2x monthly)',
        'Wheel cleaning',
        'Tire shine',
        'Priority scheduling',
        'Cancel anytime',
      ],
      featured: false,
      cta: 'Join Basic',
      optionLabel: 'Basic Wash Membership',
    },
    {
      id: 'premium',
      icon: 'sparkle',
      name: 'Premium Detailing',
      billing: 'Billed monthly · cancel anytime',
      bestFor: 'Best for commuters & families',
      features: [
        'Complete exterior wash (2x monthly)',
        'Professional wheel detailing',
        'Tire shine & protectant',
        'Interior vacuuming (2x monthly)',
        'Air freshener refresh',
        'Undercarriage rinse',
        'Priority scheduling',
        'Cancel anytime',
      ],
      badge: 'Most popular',
      featured: true,
      cta: 'Join Premium',
      optionLabel: 'Premium Detailing Membership',
    },
    {
      id: 'elite',
      icon: 'crown',
      name: 'Elite Care',
      billing: 'Billed monthly · cancel anytime',
      bestFor: 'Best for car enthusiasts',
      intro: 'Everything in Premium, plus:',
      features: [
        'Premium detail (2x monthly)',
        'Interior door jamb cleaning',
        'Engine bay rinse',
        'One free add-on service monthly',
        'First pick of appointment times',
      ],
      featured: false,
      cta: 'Join Elite',
      optionLabel: 'Elite Care Membership',
    },
  ] as MembershipPlan[],
  benefitsTitle: 'Why join our membership?',
  benefits: [
    { icon: 'piggy' as BenefitIcon, text: 'Save up to 30% vs. single washes' },
    { icon: 'calendar' as BenefitIcon, text: 'Guaranteed 2x monthly cleaning' },
    { icon: 'unlock' as BenefitIcon, text: 'No commitment — cancel anytime' },
    { icon: 'repeat' as BenefitIcon, text: 'Automatic scheduling' },
    { icon: 'zap' as BenefitIcon, text: 'Priority service, shorter waits' },
    { icon: 'message' as BenefitIcon, text: 'Book by WhatsApp in seconds' },
    { icon: 'tag' as BenefitIcon, text: 'Member discounts on add-ons' },
    { icon: 'shield' as BenefitIcon, text: "Protect your car's value long-term" },
  ],
  stepsTitle: 'Getting started is easy',
  steps: [
    { title: 'Choose your plan', text: 'Pick the tier that fits how you drive.' },
    { title: 'Sign up', text: 'Send the form or call — we set up your monthly billing.' },
    { title: 'Auto-schedule', text: 'We lock in your 2 washes, or you book as needed.' },
    { title: 'Enjoy', text: 'A consistently clean car, without the hassle.' },
  ],
  cta: {
    title: 'Start your membership today',
    subtitle: 'Professional car washing twice a month, guaranteed.',
    description: 'Your first wash is scheduled as soon as you sign up.',
    button: 'Join Membership',
    pricingNote:
      "For exact pricing and current promotions, tap 'Join' or give us a call. We offer flexible plans for every budget.",
    questions: 'Questions? Call us at',
  },
  faqTitle: 'Membership questions',
  faq: [
    {
      q: 'Can I cancel anytime?',
      a: 'Yes — there is no lock-in contract. Cancel anytime by calling or messaging us.',
    },
    {
      q: 'What if I travel and miss a wash?',
      a: 'Unused washes roll over to the next month. We store up to 3 unused washes.',
    },
    {
      q: 'Can I upgrade or downgrade?',
      a: 'Yes, change your plan anytime. The price difference is prorated.',
    },
    {
      q: 'Do I need to schedule each wash?',
      a: 'No. We can auto-schedule both washes every month, or you can book as needed.',
    },
    {
      q: 'How is my membership billed?',
      a: 'Credit card, debit card or ACH bank transfer, charged automatically each month.',
    },
    {
      q: 'Are there any other fees?',
      a: 'No. Your membership price covers everything included in your plan — no hidden fees.',
    },
  ],
}

export type MembershipCopy = typeof en

const es: MembershipCopy = {
  kicker: 'Membresía',
  title: 'Planes de Membresía Mensual',
  subtitle:
    'Ahorra dinero. Mantén tu carro impecable. Dos lavados garantizados cada mes.',
  description:
    'Un precio mensual bajo, dos lavados profesionales cada mes — agendados automáticamente y siempre puntuales. Ideal para quienes quieren un carro limpio sin preocuparse.',
  perWash: '2 lavados / mes',
  plans: [
    {
      id: 'basic',
      icon: 'droplet',
      name: 'Lavado Básico',
      billing: 'Cobro mensual · cancela cuando quieras',
      bestFor: 'Ideal para uso diario con presupuesto',
      features: [
        'Lavado exterior (2x al mes)',
        'Limpieza de rines',
        'Brillo de llantas',
        'Agenda prioritaria',
        'Cancela cuando quieras',
      ],
      featured: false,
      cta: 'Unirme a Básico',
      optionLabel: 'Membresía Lavado Básico',
    },
    {
      id: 'premium',
      icon: 'sparkle',
      name: 'Detallado Premium',
      billing: 'Cobro mensual · cancela cuando quieras',
      bestFor: 'Ideal para quien maneja a diario y familias',
      features: [
        'Lavado exterior completo (2x al mes)',
        'Detallado profesional de rines',
        'Brillo y protector de llantas',
        'Aspirado interior (2x al mes)',
        'Aromatizante renovado',
        'Enjuague de chasis',
        'Agenda prioritaria',
        'Cancela cuando quieras',
      ],
      badge: 'Más popular',
      featured: true,
      cta: 'Unirme a Premium',
      optionLabel: 'Membresía Detallado Premium',
    },
    {
      id: 'elite',
      icon: 'crown',
      name: 'Cuidado Elite',
      billing: 'Cobro mensual · cancela cuando quieras',
      bestFor: 'Ideal para amantes de los autos',
      intro: 'Todo lo de Premium, más:',
      features: [
        'Detallado premium (2x al mes)',
        'Limpieza de marcos de puertas',
        'Enjuague de motor',
        'Un servicio adicional gratis al mes',
        'Primera opción de horarios',
      ],
      featured: false,
      cta: 'Unirme a Elite',
      optionLabel: 'Membresía Cuidado Elite',
    },
  ],
  benefitsTitle: '¿Por qué unirte a la membresía?',
  benefits: [
    { icon: 'piggy', text: 'Ahorra hasta 30% vs. lavados sueltos' },
    { icon: 'calendar', text: 'Dos lavados garantizados al mes' },
    { icon: 'unlock', text: 'Sin compromiso — cancela cuando quieras' },
    { icon: 'repeat', text: 'Agenda automática' },
    { icon: 'zap', text: 'Servicio prioritario, menos espera' },
    { icon: 'message', text: 'Reserva por WhatsApp en segundos' },
    { icon: 'tag', text: 'Descuentos en servicios adicionales' },
    { icon: 'shield', text: 'Protege el valor de tu carro' },
  ],
  stepsTitle: 'Empezar es muy fácil',
  steps: [
    { title: 'Elige tu plan', text: 'Escoge el nivel que va con tu forma de manejar.' },
    { title: 'Inscríbete', text: 'Envía el formulario o llámanos — configuramos tu cobro mensual.' },
    { title: 'Agenda automática', text: 'Apartamos tus 2 lavados, o reservas cuando quieras.' },
    { title: 'Disfruta', text: 'Un carro siempre limpio, sin complicaciones.' },
  ],
  cta: {
    title: 'Empieza tu membresía hoy',
    subtitle: 'Lavado profesional dos veces al mes, garantizado.',
    description: 'Tu primer lavado se agenda en cuanto te inscribes.',
    button: 'Unirme a la Membresía',
    pricingNote:
      'Para precios exactos y promociones vigentes, toca "Unirme" o llámanos. Tenemos planes flexibles para cada presupuesto.',
    questions: '¿Dudas? Llámanos al',
  },
  faqTitle: 'Preguntas sobre la membresía',
  faq: [
    {
      q: '¿Puedo cancelar cuando quiera?',
      a: 'Sí — no hay contrato forzoso. Cancela cuando quieras llamándonos o por mensaje.',
    },
    {
      q: '¿Y si viajo y no uso un lavado?',
      a: 'Los lavados no usados pasan al siguiente mes. Guardamos hasta 3 lavados.',
    },
    {
      q: '¿Puedo subir o bajar de plan?',
      a: 'Sí, cambia de plan cuando quieras. La diferencia se prorratea.',
    },
    {
      q: '¿Tengo que agendar cada lavado?',
      a: 'No. Podemos agendar tus dos lavados automáticamente cada mes, o reservas cuando quieras.',
    },
    {
      q: '¿Cómo se cobra la membresía?',
      a: 'Tarjeta de crédito, débito o transferencia ACH, cobrado automáticamente cada mes.',
    },
    {
      q: '¿Hay otros cargos?',
      a: 'No. El precio de tu membresía cubre todo lo incluido en tu plan — sin cargos ocultos.',
    },
  ],
}

export const membershipCopy = { en, es }
